import { gql, request, useQuery } from '~reactQuery';

import { CAPACCOUNT_QUERY_KEYS } from '~src/constants/capaccount';
import { StatementUnion } from '~reactQuery/types/bank.types';

export interface GetStatementSpecificInput {
  authCode: string;
  userOrganizationId: number;
}

export interface GetStatementSpecificResponse {
  statementSpecific: {
    authCode: string;
    data: StatementUnion;
    direction: number;
    method: string;
    status: string;
    timestamp: string;
  };
}

export const useGetStatementSpecific = (params: GetStatementSpecificInput) => {
  const QUERY = gql`
    query StatementSpecific($params: StatementSpecificInput!) {
      statementSpecific(params: $params) {
        authCode
        timestamp
        direction
        method
        status
        data {
          ... on Movement {
            value
            description
            method
            requisite {
              ... on BankAccount {
                bank {
                  bankName
                  bankIspb
                  bankCompe
                }
                branch
                account
                holderName
                holderDocument
                accountType
              }
              ... on PixKeyMovement {
                key {
                  bank {
                    bankName
                    bankIspb
                    bankCompe
                  }
                  branch
                  account
                  holderName
                  holderDocument
                  accountType
                  e2eId
                }
                keyType
              }
              ... on Exchange {
                type
                walletAddress
              }
            }
          }
          ... on BillPayment {
            description
            digitableLine
            originalValue
            fine
            interest
            discount
            value
            dueDate
            settleDate
            assignor
            recipientName
            recipientDocument
          }
          ... on CardTransaction {
            value
            description
            method
            details {
              cardId
              amount
              category
              city
              stateOrCountryCode
              merchantName
              transactionType
              zip
              authorizationTransactionId
              transactionStatus
              amountSettlement
              amountLocal
              markup
              iof
              dollarExchangeRate
              entryMode
              has3Ds
              acquirerCode
              merchantTerminalId
              currencyCode
              mcc
              cardAlias
              authorizationCode
              withdrawalFeeAmount
              deniedRules
              responseCode
              walletId
              isPre
              isPos
              isDebit
              numberOfInstallments
              correlationId
              document
              companyKey
              eventDateTime
            }
          }
          ... on Invoice {
            description
            value
            method
          }
        }
      }
    }
  `;

  const fetcher = async () => {
    if (!params.authCode) return null;

    const response = await request<GetStatementSpecificResponse>(QUERY, {
      params,
    });
    return response.statementSpecific;
  };

  return useQuery(
    [CAPACCOUNT_QUERY_KEYS.GET_CAPACCOUNT_STATEMENT_SPECIFIC, params.authCode],
    fetcher,
    {
      cacheTime: 0,
      staleTime: 0,
    }
  );
};
